import { useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Lock, Eye, EyeOff, Check, AlertTriangle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ChangePasswordPage = () => {
  const navigate = useNavigate();
  const [form, setForm] = useState({ current: '', next: '', confirm: '' });
  const [show, setShow] = useState({ current: false, next: false, confirm: false });
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState(null);

  const strength = (() => {
    const p = form.next;
    if (!p) return { level: 0, label: '' };
    let score = 0;
    if (p.length >= 8) score++;
    if (p.length >= 12) score++;
    if (/[A-Z]/.test(p) && /[a-z]/.test(p)) score++;
    if (/\d/.test(p)) score++;
    if (/[^A-Za-z0-9]/.test(p)) score++;
    const labels = ['Très faible', 'Faible', 'Moyen', 'Bon', 'Excellent'];
    return { level: Math.min(score, 4), label: labels[Math.min(score, 4)] };
  })();

  const submit = async (e) => {
    e.preventDefault();
    setErrorMsg(null);
    if (form.next.length < 8) return setErrorMsg('Le nouveau mot de passe doit faire au moins 8 caractères');
    if (form.next !== form.confirm) return setErrorMsg('Les deux nouveaux mots de passe ne sont pas identiques');
    if (form.next === form.current) return setErrorMsg("Le nouveau mot de passe doit être différent de l'ancien");

    setLoading(true);
    try {
      await axios.post(`${API}/auth/change-password`, {
        current_password: form.current,
        new_password: form.next,
      }, { withCredentials: true });
      toast.success('Mot de passe modifié avec succès');
      setTimeout(() => navigate('/dashboard'), 800);
    } catch (err) {
      setErrorMsg(err.response?.data?.detail || 'Erreur — vérifiez votre mot de passe actuel');
    } finally {
      setLoading(false);
    }
  };

  const barColors = ['bg-red-500', 'bg-orange-500', 'bg-amber-500', 'bg-lime-500', 'bg-emerald-500'];

  return (
    <div className="max-w-lg mx-auto" data-testid="change-password-page">
      <div className="flex items-center gap-3 mb-6">
        <div className="p-3 bg-emerald-100 rounded-xl">
          <Lock className="h-6 w-6 text-emerald-600" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Changer mon mot de passe</h1>
          <p className="text-sm text-gray-500">Choisis un mot de passe simple pour toi mais difficile à deviner.</p>
        </div>
      </div>

      {errorMsg && (
        <Card className="border-red-300 bg-red-50 mb-4" data-testid="change-password-error">
          <CardContent className="p-3 flex items-start gap-2">
            <AlertTriangle className="h-4 w-4 text-red-600 mt-0.5 flex-shrink-0" />
            <p className="text-sm text-red-800">{errorMsg}</p>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Sécurité du compte</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={submit} className="space-y-4">
            {/* Ancien MDP */}
            <div className="space-y-2">
              <Label htmlFor="current">Mot de passe actuel</Label>
              <div className="relative">
                <Input
                  id="current"
                  type={show.current ? 'text' : 'password'}
                  value={form.current}
                  onChange={(e) => setForm(f => ({ ...f, current: e.target.value }))}
                  placeholder="Ton MDP actuel (ex: CPXXXXXX si tu viens du forgot-password)"
                  required
                  autoComplete="current-password"
                  data-testid="input-current-password"
                />
                <button type="button" onClick={() => setShow(s => ({ ...s, current: !s.current }))}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-gray-700">
                  {show.current ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            {/* Nouveau MDP */}
            <div className="space-y-2">
              <Label htmlFor="next">Nouveau mot de passe</Label>
              <div className="relative">
                <Input
                  id="next"
                  type={show.next ? 'text' : 'password'}
                  value={form.next}
                  onChange={(e) => setForm(f => ({ ...f, next: e.target.value }))}
                  placeholder="Au moins 8 caractères"
                  required
                  autoComplete="new-password"
                  data-testid="input-new-password"
                />
                <button type="button" onClick={() => setShow(s => ({ ...s, next: !s.next }))}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-gray-700">
                  {show.next ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              {form.next && (
                <div className="space-y-1">
                  <div className="flex gap-1">
                    {[0, 1, 2, 3, 4].map(i => (
                      <div key={i} className={`h-1 flex-1 rounded ${i <= strength.level ? barColors[strength.level] : 'bg-gray-200'}`} />
                    ))}
                  </div>
                  <p className="text-xs text-gray-500">Force : <strong>{strength.label}</strong></p>
                </div>
              )}
            </div>

            {/* Confirmation */}
            <div className="space-y-2">
              <Label htmlFor="confirm">Confirme le nouveau mot de passe</Label>
              <div className="relative">
                <Input
                  id="confirm"
                  type={show.confirm ? 'text' : 'password'}
                  value={form.confirm}
                  onChange={(e) => setForm(f => ({ ...f, confirm: e.target.value }))}
                  placeholder="Retape le même"
                  required
                  autoComplete="new-password"
                  data-testid="input-confirm-password"
                />
                <button type="button" onClick={() => setShow(s => ({ ...s, confirm: !s.confirm }))}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-gray-700">
                  {show.confirm ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              {form.confirm && (
                <p className={`text-xs flex items-center gap-1 ${form.next === form.confirm ? 'text-emerald-600' : 'text-red-600'}`}>
                  {form.next === form.confirm ? <><Check className="h-3 w-3" /> Identique</> : <>Les mots de passe ne correspondent pas</>}
                </p>
              )}
            </div>

            <Button
              type="submit"
              size="lg"
              className="w-full bg-emerald-600 hover:bg-emerald-700"
              disabled={loading}
              data-testid="btn-submit-change-password"
            >
              {loading ? 'Enregistrement...' : 'Enregistrer le nouveau mot de passe'}
            </Button>

            <p className="text-xs text-gray-500 text-center pt-2">
              Astuce : utilise une phrase mémorable + chiffres. Ex : <em>Capsule2026Facturation!</em>
            </p>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default ChangePasswordPage;
